import axios from 'axios'
import {
  ProgramPurseListSchema,
  ProgramPurseResourceSchema,
  GetPurseDistributionsResponseSchema,
  ProgramPurseAffiliationsResponseSchema,
  AwardedStudentsPreview,
  AwardedStudentsPreviewSchema,
  FundAwardedStudentsResponse,
  FundAwardedStudentsResponseSchema,
} from '@/queries/program_purses/schemas.ts'
import {
  parseBlobRequestError,
  parseRequestError,
} from '@/app/common/utilities.ts'
import { z } from 'zod'

export type UpdatePurseAcademicTermTypeParams = {
  programId: number
  purseId: number
  academicTermTypeId: number
}

export const updatePurseAcademicTermType = async ({
  programId,
  purseId,
  academicTermTypeId,
}: UpdatePurseAcademicTermTypeParams) => {
  try {
    await axios.put(`/programs/${programId}/purses/${purseId}`, {
      academic_term_type_id: academicTermTypeId,
    })
  } catch (error) {
    throw parseRequestError(error)
  }
}

export type GetProgramPursesParams = {
  programId: number
}

export const getProgramPurses = async ({
  programId,
}: GetProgramPursesParams) => {
  try {
    const response = await axios.get(`/programs/${programId}/purses`)
    return ProgramPurseListSchema.parse(response.data)
  } catch (error) {
    throw parseRequestError(error)
  }
}

export type GetPurseParams = {
  purseId: string
  programId: number
}
export const getPurseById = async ({ purseId, programId }: GetPurseParams) => {
  const response = await axios.get(`/programs/${programId}/purses/${purseId}`)
  return ProgramPurseResourceSchema.parse(response.data)
}

type PurseDistributionsParams = {
  programId: number
  purseId: number
}

export const getPurseDistributions = async (
  params: PurseDistributionsParams
) => {
  const { programId, purseId } = params
  const response = await axios.get(
    `/programs/${programId}/purses/${purseId}/list_distributions`
  )
  return GetPurseDistributionsResponseSchema.parse(response.data)
}

export type DownloadDistributionReportParams = {
  programId: number
  purseId: number
  distributionId: number
  password: string
}

export const downloadDistributionReport = async (
  params: DownloadDistributionReportParams
) => {
  const { programId, purseId, distributionId, password } = params
  const response = await axios.post(
    `/programs/${programId}/purses/${purseId}/distributions/${distributionId}/download_distribution_report`,
    { password },
    { responseType: 'blob' }
  )

  return response.data
}

type PurseAffiliationsParams = {
  programId: number
  purseId: number
}

export const getPurseAffiliations = async (params: PurseAffiliationsParams) => {
  try {
    const { programId, purseId } = params
    const response = await axios.get(
      `/programs/${programId}/purses/${purseId}/settings/affiliations`
    )

    return ProgramPurseAffiliationsResponseSchema.parse(response.data)
  } catch (error) {
    throw parseRequestError(error)
  }
}

type SyncPurseAffiliationsParams = PurseAffiliationsParams & {
  affiliationIds: number[]
}

export const syncPurseAffiliations = async (
  params: SyncPurseAffiliationsParams
) => {
  try {
    const { programId, purseId, affiliationIds } = params
    const response = await axios.post(
      `/programs/${programId}/purses/${purseId}/settings/affiliations`,
      {
        affiliation_ids: affiliationIds,
      }
    )

    return ProgramPurseAffiliationsResponseSchema.parse(response.data)
  } catch (error) {
    throw parseRequestError(error)
  }
}

export type TransferPurseFundsParams = {
  programId: number
  purseId: number
  amount: number
  targetProgramPurseId: number
}

export const transferPurseFunds = async ({
  programId,
  purseId,
  amount,
  targetProgramPurseId,
}: TransferPurseFundsParams) => {
  try {
    const response = await axios.post(
      `/programs/${programId}/purses/${purseId}/transfer`,
      {
        amount,
        target_program_purse_id: targetProgramPurseId,
      }
    )

    return response.data
  } catch (error) {
    throw parseRequestError(error)
  }
}

export type RemovePurseFundsParams = {
  programId: number
  purseId: number
  amount: number
}

export const removePurseFunds = async ({
  programId,
  purseId,
  amount,
}: RemovePurseFundsParams) => {
  try {
    const response = await axios.post(
      `/programs/${programId}/purses/${purseId}/remove_funds`,
      {
        amount,
      }
    )

    return response.data
  } catch (error) {
    throw parseRequestError(error)
  }
}

export type AwardedStudentsPreviewParams = {
  programId: number
  purseId: number
}

/**
 * Summarises what `fundAwardedStudents` is about to fund: how many students ServiceNow put in
 * Awarded status on the purse and the total of the amounts it supplied. Read-only — it neither
 * awards nor funds anything.
 */
export const getAwardedStudentsPreview = async ({
  programId,
  purseId,
}: AwardedStudentsPreviewParams): Promise<AwardedStudentsPreview> => {
  try {
    const response = await axios.get(
      '/scholarships/distribution/plan_preview',
      {
        params: {
          program_id: programId,
          program_purse_id: purseId,
        },
      }
    )

    return AwardedStudentsPreviewSchema.parse(response.data)
  } catch (error) {
    throw parseRequestError(error)
  }
}

export type DownloadAwardedStudentsReportParams = {
  programId: number
  purseId: number
}

/** CSV with one row per awarded student, using the amounts ServiceNow supplied. */
export async function downloadAwardedStudentsReport({
  programId,
  purseId,
}: DownloadAwardedStudentsReportParams) {
  try {
    const response = await axios.get(
      '/scholarships/distribution/download_distribution_preview_report',
      {
        params: {
          program_id: programId,
          program_purse_id: purseId,
        },
        responseType: 'blob',
      }
    )

    return response.data
  } catch (error) {
    // The error body arrives as a Blob on this request, so it has to be read back as text
    // before the API message can reach the toast.
    throw new Error((await parseBlobRequestError(error)).message)
  }
}

export type FundAwardedStudentsParams = {
  programId: number
  purseId: number
  /**
   * Restricts funding to these students. Omit to fund every student in `Awarded` status on the
   * purse. Every id sent must already be Awarded on this purse for the active term — the API
   * rejects the whole request and funds nobody otherwise.
   */
  studentIds?: number[]
}

/**
 * Funds students sitting in `Awarded` status on the purse, using the exact award amount
 * ServiceNow supplied. The platform neither derives nor overrides amounts. Idempotent: students
 * already funded for the purse are skipped instead of double funded.
 */
export const fundAwardedStudents = async ({
  programId,
  purseId,
  studentIds,
}: FundAwardedStudentsParams): Promise<FundAwardedStudentsResponse> => {
  try {
    const response = await axios.post(
      `/programs/${programId}/purses/${purseId}/fund_awarded_students`,
      studentIds?.length ? { student_ids: studentIds } : undefined
    )

    return FundAwardedStudentsResponseSchema.parse(response.data)
  } catch (error) {
    throw parseRequestError(error)
  }
}

export type UpdateStudentScholarshipAwardParams = {
  programId: number
  studentId: number
  amount: number
  programPurseId: number
}

const UpdateStudentScholarshipAwardResponseSchema = z.object({
  message: z.string(),
  new_amount: z.number(),
})

export type UpdateStudentScholarshipAwardResponse = z.infer<
  typeof UpdateStudentScholarshipAwardResponseSchema
>

export const updateStudentScholarshipAward = async ({
  programId,
  studentId,
  amount,
  programPurseId,
}: UpdateStudentScholarshipAwardParams): Promise<UpdateStudentScholarshipAwardResponse> => {
  try {
    const response = await axios.patch('student_scholarship_award', {
      program_id: programId,
      student_id: studentId,
      amount,
      program_purse_id: programPurseId,
    })

    return UpdateStudentScholarshipAwardResponseSchema.parse(response.data)
  } catch (error) {
    throw parseRequestError(error)
  }
}
